import type { FC } from "react";
import {
	type ExpenseAmountFormModel,
	type ExpenseAmountValues,
	useExpenseAmountForm,
} from "web_myplann/Finances/Forms/useExpenseAmountForm";
import type {
	ClientFinancialPlanExpenseNameEnum,
	ClientFinancialPlanExpenseTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FormAmountStepperField } from "web_myplann/Shared/Components/FormAmountStepperField";
import { FormSheet } from "web_myplann/Shared/Components/FormSheet";

const R = Resources.Finances.ExpenseSheet;

type Props = {
	target: {
		type: ClientFinancialPlanExpenseTypeEnum;
		name: ClientFinancialPlanExpenseNameEnum;
	} | null;
	isEditing: boolean;
	initialValues?: ExpenseAmountValues;
	onClose: () => void;
	onSubmit: (values: ExpenseAmountFormModel) => void;
};

export const ExpenseAmountFormSheet: FC<Props> = ({
	target,
	isEditing,
	initialValues,
	onClose,
	onSubmit,
}) => {
	const { t } = useResource();
	const open = target !== null;
	const {
		control,
		handleSubmit,
		formState: { isValid },
	} = useExpenseAmountForm({ open, initialValues });

	const title = target
		? `${t(Resources.Finances.Expenses.Types[target.type].title)} - ${t(Resources.Finances.Expenses.Names[target.name])}`
		: "";

	return (
		<FormSheet
			open={open}
			title={title}
			submitLabel={isEditing ? t(Resources.Common.save) : t(R.add)}
			submitDisabled={!isValid}
			onClose={onClose}
			onSubmit={handleSubmit(onSubmit)}
		>
			<FormAmountStepperField
				control={control}
				name="monthlyPayment"
				label={t(R.value)}
			/>
			<FormAmountStepperField
				control={control}
				name="sustainableAmount"
				label={t(R.minimumValue)}
				hint={t(R.description)}
			/>
		</FormSheet>
	);
};
